import type React from 'react';
import Link from 'next/link';
import ReviewsV2Word from './ReviewsV2Word';
import type { SiteStats } from '@/lib/siteStats';

export default function Footer({ stats = null, lean = false }: { stats?: SiteStats | null; lean?: boolean }) {
  const n = (v: number) => v.toLocaleString('he-IL');
  return (
    <footer style={{ background: 'var(--sk-band, linear-gradient(160deg, #0d47a1 0%, #1565c0 50%, #0a3d8f 100%))' }}>
      <div className={`max-w-4xl md:max-w-6xl mx-auto px-6 ${lean ? "pt-8 pb-24 md:pt-6 md:pb-5" : "pt-12 pb-8 md:pt-6 md:pb-5"}`}>

        {/* md+ (28/10/26 round 5): compact — ONE row [logo + slogan | values inline], then ONE row [links | copyright]; phones unchanged */}
        <div className="md:flex md:items-center md:justify-between md:gap-6">

        {/* Brand — centered, like the header */}
        <div className="text-center mb-8 md:mb-0 md:flex md:items-center md:gap-3 md:text-start md:flex-none">
          <picture>
            <source srcSet="/brand/logo-white.avif" type="image/avif" />
            <source srcSet="/brand/logo-white.webp" type="image/webp" />
            <img
              src="/brand/logo-white.webp"
              alt="המומחים"
              width={227}
              height={38}
              className="h-[45px] md:h-[30px] w-auto mx-auto md:mx-0"
              style={{ objectFit: 'contain' }}
              loading="lazy"
            />
          </picture>
          {/* round 6 (28/10/26): live counts (lib/siteStats.ts, cached 1h); the old slogan only when the DB didn't answer */}
          <p className="text-sm font-semibold text-white mt-2 md:mt-0">
            {stats ? <>מנתחים {n(stats.reviews)} המלצות מ-Google של {n(stats.pros)} מומחים בשבילך בחינם</> : 'לקבל בחינם הצעות ממומחים מומלצים לידך'}
          </p>
        </div>

        {/* למה אנחנו — centered list, no heading */}
        {!lean && <ul className="text-center space-y-3 max-w-xs mx-auto mb-10 md:max-w-none md:mx-0 md:mb-0 md:space-y-0 md:flex md:flex-wrap md:justify-end md:gap-x-5 md:gap-y-1">
          {/* "ביקורות" → "המלצות" on a reviews-v2 preview pro page (components/ReviewsV2Word.tsx) */}
          {([
            ['objective', <>⭐️ <ReviewsV2Word /> אובייקטיביות בלבד</>],
            ['verified', <>✅ <ReviewsV2Word /> מאומתות בלבד</>],
            ['free', '🎁 זה בחינם'],
            ['fast', '🚀 תגובה מהירה'],
            ['love', '❤️ נעשה באהבה :)'],
          ] as [string, React.ReactNode][]).map(([k, t]) => (
            <li key={k} className="text-sm font-semibold text-white md:text-[13px]">{t}</li>
          ))}
        </ul>}
        </div>

        {/* Bottom bar — site links + copyright */}
        <div className="border-t pt-6 text-center md:mt-5 md:pt-4 md:flex md:items-center md:justify-between md:gap-4" style={{ borderColor: 'rgba(255,255,255,.12)' }}>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-4 md:mb-0 md:justify-start">
            {[
              ...(lean ? [['איך זה עובד', '/#how'], ['כל הקטגוריות', '/קטגוריה'], ['למומחים: הצטרפות', '/הצטרף']] : []),
              ['אודות', '/אודות'],
              ['מאמרים', '/מאמרים'],
              ['צור קשר', '/צור-קשר'],
              ['פרטיות', '/פרטיות'],
              ['תנאי משתמש', '/תנאים'],
            ].map(([n, h]) => (
              <Link key={n} href={h} className="text-sm transition-all hover:text-white"
                style={{ color: 'rgba(255,255,255,.85)', fontWeight: lean ? 500 : 300, ...(lean ? { display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 4px' } : {}) }}>
                {n}
              </Link>
            ))}
          </div>
          <p className="text-sm md:text-[13px] md:flex-none" style={{ color: 'rgba(255,255,255,.55)', fontWeight: 300 }}>
            © 2026 המומחים · כל הזכויות שמורות
          </p>
        </div>
      </div>
    </footer>
  );
}
